import React from 'react';
import CopyIcon from '../assets/icons/copy.svg';

interface CopiableTextProps {
    value: string;
    displayText?: string;
    className?: string;
    successDurationMs?: number;
}

const CopiableText: React.FC<CopiableTextProps> = ({
    value,
    displayText,
    className,
    successDurationMs = 1500
}) => {
    const [copied, setCopied] = React.useState(false);
    const timeoutRef = React.useRef<number | null>(null);

    const textToShow = displayText ?? value;
    const isDisabled = !value;

    const handleCopy = () => {
        if (isDisabled) return;
        navigator.clipboard
            .writeText(value)
            .then(() => {
                setCopied(true);
                if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
                timeoutRef.current = window.setTimeout(() => setCopied(false), successDurationMs);
            })
            .catch(() => {
                /* clipboard permission denied - silently no-op */
            });
    };

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
        };
    }, []);

    return (
        <button
            type="button"
            aria-disabled={isDisabled}
            onClick={handleCopy}
            className={`items-center flex flex-row justify-start gap-1 text-sm ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${className ?? ''}`}
        >
            <span className="text-[#3773A8] font-semibold">{textToShow || 'N/A'}</span>
            {copied ? (
                <span className="flex items-center gap-1 text-green-600 font-semibold">
                    <svg
                        className="w-4 h-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                    Copied
                </span>
            ) : (
                <img src={CopyIcon} alt="Copy" className="w-4 h-4" />
            )}
            <span className="sr-only" aria-live="polite">
                {copied ? 'Copied to clipboard' : ''}
            </span>
        </button>
    );
};

export default CopiableText;
